import { useState, useEffect } from 'react';
import { getUser, logout } from '../../services/auth';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FaUsers, FaClipboardList, FaCreditCard } from 'react-icons/fa';
import { MdMenu, MdClose, MdHome } from 'react-icons/md';
import api from '../../services/api';
import logoImage from '../../assets/babysit_logo.png';
import { useIsMobile } from '../../hooks/useIsMobile';

const adminNavItems = [
  { path: '/dashboard/admin', tab: null, icon: MdHome, label: 'Dashboard' },
  { path: '/admin', tab: 'users', icon: FaUsers, label: 'Korisnici' },
  { path: '/admin', tab: 'jobs', icon: FaClipboardList, label: 'Poslovi' },
  { path: '/admin', tab: 'payments', icon: FaCreditCard, label: 'Plaćanja' },
];

export default function AdminDashboard() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalJobs: 0,
    activeJobs: 0,
    totalRevenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = getUser();
    if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'platform_admin')) {
      navigate('/login');
      return;
    }
    setUser(currentUser);
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      const [usersRes, jobsRes] = await Promise.all([
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/jobs?limit=1000').catch(() => ({ data: { jobs: [] } })),
      ]);
      const users = Array.isArray(usersRes.data) ? usersRes.data : [];
      const jobs = jobsRes.data?.jobs || (Array.isArray(jobsRes.data) ? jobsRes.data : []);
      setStats({
        totalUsers: users.length,
        totalJobs: jobs.length,
        activeJobs: jobs.filter((j) => j.status === 'accepted' || j.status === 'in_progress').length,
        totalRevenue: jobs.reduce((sum, job) => sum + (job.hourlyRate || 0), 0),
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="px-4 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Link to="/dashboard/admin" className="flex items-center gap-2 min-w-0 shrink-0">
              <img src={logoImage} alt="Nannity" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-primary-600 dark:text-primary-300 truncate">Nannity</span>
            </Link>
            <Link to="/admin" className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">
              <MdHome className="text-lg" /> Panel
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[140px]">
              {user?.firstName} {user?.lastName}
            </span>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              Odjavi se
            </button>
          </div>
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Meni"
          >
            {mobileMenuOpen ? <MdClose className="text-xl" /> : <MdMenu className="text-xl" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 flex flex-col gap-2">
            <Link to="/admin" className="flex items-center gap-2 py-2 text-gray-700 dark:text-gray-300" onClick={() => setMobileMenuOpen(false)}>
              <MdHome /> Admin Panel
            </Link>
            <p className="text-sm text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-700">
              {user?.firstName} {user?.lastName}
            </p>
            <button onClick={() => { setMobileMenuOpen(false); handleLogout(); }} className="text-left py-2 text-red-600 dark:text-red-400">
              Odjavi se
            </button>
          </div>
        )}
      </header>

      <main className={`container mx-auto px-4 py-8 ${isMobile ? 'pb-24' : ''}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="card">
            <h3 className="text-sm text-gray-500 dark:text-gray-400 mb-2">Ukupno korisnika</h3>
            <p className="text-2xl md:text-3xl font-bold text-primary-600 dark:text-primary-400">{stats.totalUsers}</p>
          </div>
          <div className="card">
            <h3 className="text-sm text-gray-500 dark:text-gray-400 mb-2">Ukupno poslova</h3>
            <p className="text-2xl md:text-3xl font-bold text-primary-600 dark:text-primary-400">{stats.totalJobs}</p>
          </div>
          <div className="card">
            <h3 className="text-sm text-gray-500 dark:text-gray-400 mb-2">Aktivni poslovi</h3>
            <p className="text-2xl md:text-3xl font-bold text-primary-600 dark:text-primary-400">{stats.activeJobs}</p>
          </div>
          <div className="card">
            <h3 className="text-sm text-gray-500 dark:text-gray-400 mb-2">Ukupna zarada (RSD)</h3>
            <p className="text-2xl md:text-3xl font-bold text-primary-600 dark:text-primary-400">{stats.totalRevenue.toLocaleString('sr-RS')}</p>
          </div>
        </div>

        {/* Brzi linkovi - skriveni na mobilu (koristi se bottom tab bar) */}
        <div className="hidden md:block card">
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">Brzi linkovi</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/admin?tab=users"
              className="p-6 border-2 border-primary-200 dark:border-primary-800 rounded-xl hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-md transition-all text-center"
            >
              <div className="flex justify-center mb-2">
                <FaUsers className="text-4xl text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Korisnici</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Upravljanje korisnicima</p>
            </Link>
            <Link
              to="/admin?tab=jobs"
              className="p-6 border-2 border-primary-200 dark:border-primary-800 rounded-xl hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-md transition-all text-center"
            >
              <div className="flex justify-center mb-2">
                <FaClipboardList className="text-4xl text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Poslovi</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Upravljanje poslovima</p>
            </Link>
            <Link
              to="/admin?tab=payments"
              className="p-6 border-2 border-primary-200 dark:border-primary-800 rounded-xl hover:border-primary-500 dark:hover:border-primary-500 hover:shadow-md transition-all text-center"
            >
              <div className="flex justify-center mb-2">
                <FaCreditCard className="text-4xl text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Plaćanja</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Pregled plaćanja</p>
            </Link>
          </div>
        </div>
      </main>

      {/* Mobile: bottom tab bar (kao u regularnom delu) */}
      {isMobile && (
        <nav className="md:hidden fixed bottom-2 left-0 right-0 z-50 px-2 pb-safe pt-2">
          <div className="max-w-lg mx-auto flex items-center justify-around gap-1 bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 px-2">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.path === '/dashboard/admin'
                ? location.pathname === '/dashboard/admin'
                : location.pathname === item.path && (item.tab ? new URLSearchParams(location.search).get('tab') === item.tab : true);
              const to = item.tab ? `${item.path}?tab=${item.tab}` : item.path;
              return (
                <Link
                  key={item.path + (item.tab || '')}
                  to={to}
                  className={`flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 py-1.5 px-2 rounded-xl transition-colors ${
                    isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Icon className="text-xl flex-shrink-0" />
                  <span className="text-[10px] font-medium truncate max-w-full">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
